import { FC, MutableRefObject, ReactElement, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { createContext } from 'use-context-selector';

import useInspections, { UseInspectionsHook } from '../hooks/useInspections';
import useSearchAmenities, { UseSearchAmenitiesHook } from '../hooks/useSearchAmenities';
import { TInspection, TProperty } from '../services/Amenities/types';
import {
  useFetchInspectionAmenities,
  useFetchProperties,
  useSyncInspection,
} from '../services/Amenities/useAmenitiesService';

interface IAmenitiesContext {
  filteredProperties: TProperty[] | undefined;
  inspectionAmenities: TInspection | undefined;
  loadingInspectionAmenities: boolean;
  loadingSyncInspection: boolean;
  errorInspectionAmenities: any;
  loadingProperties: boolean;
  selectedProperty: string;
  selectedPropertyRef: MutableRefObject<string>;
  handleChangeProperty: (value: string) => void;
  handleSearch: () => void;
  handleSyncInspection: () => void;
  searchAmenities: UseSearchAmenitiesHook;
  inspections: UseInspectionsHook;
  version?: 'v1' | 'v2' | undefined;
}

export const AmenitiesContext = createContext<IAmenitiesContext>({
  filteredProperties: undefined,
  inspectionAmenities: undefined,
  loadingInspectionAmenities: false,
  loadingSyncInspection: false,
  errorInspectionAmenities: null,
  loadingProperties: false,
  selectedProperty: '',
  selectedPropertyRef: { current: '' },
  handleChangeProperty: () => {},
  handleSearch: () => {},
  handleSyncInspection: () => {},
  searchAmenities: {} as UseSearchAmenitiesHook,
  inspections: {} as UseInspectionsHook,
  version: undefined,
});

export const getAmenitiesVersion = (): 'v1' | 'v2' => {
  return 'v2';
};

export const AmenitiesProvider: FC<{ children: ReactElement }> = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();

  const { data: properties, loading: loadingProperties, fetchProperties } = useFetchProperties();
  const {
    data: inspectionAmenities,
    loading: loadingInspectionAmenities,
    error: errorInspectionAmenities,
    fetchInspectionAmenities,
  } = useFetchInspectionAmenities();

  const searchAmenities = useSearchAmenities({ inspectionAmenities });
  const {
    success: successSyncInspection,
    loading: loadingSyncInspection,
    syncInspection,
  } = useSyncInspection();

  const inspections = useInspections();

  const [selectedProperty, setSelectedProperty] = useState<string>('');
  const selectedPropertyRef = useRef<string>(selectedProperty);
  const hasSyncedRef = useRef(false);

  const [version, setVersion] = useState<'v1' | 'v2'>();

  const filteredProperties = useMemo(() => {
    if (!properties) return undefined;
    let filtered = properties.filter((p) => p.status !== 'Inactive');

    if (inspections.searchTerm.trim()) {
      const term = inspections.searchTerm.trim().toLowerCase();
      filtered = filtered.filter((p) => {
        if (inspections.searchField === 'property_code') {
          return p.code.toLowerCase().includes(term);
        }
        if (inspections.searchField === 'host_name') {
          return p.hostName?.toLowerCase().includes(term) ?? false;
        }
        return false;
      });
    }

    return filtered;
  }, [properties, inspections.searchTerm, inspections.searchField]);

  const handleSyncInspection = async () => {
    if (selectedProperty) {
      selectedPropertyRef.current = selectedProperty;
      await syncInspection(selectedProperty);
    }
  };

  const handleChangeProperty = (value: string) => {
    setSelectedProperty(value);
  };

  const handleSearch = async () => {
    if (!selectedProperty) return;

    selectedPropertyRef.current = selectedProperty;
    const response = await fetchInspectionAmenities(selectedProperty);

    if (!response && !hasSyncedRef.current) {
      hasSyncedRef.current = true;
      await syncInspection(selectedProperty);
    } else {
      hasSyncedRef.current = false;
    }
  };

  useEffect(() => {
    if (version === 'v2' && selectedProperty) {
      selectedPropertyRef.current = selectedProperty;
      handleSearch();
    }
  }, [selectedProperty]);

  useEffect(() => {
    setVersion(getAmenitiesVersion());
  }, []);

  useEffect(() => {
    fetchProperties();
  }, [fetchProperties]);

  useEffect(() => {
    if (!loadingSyncInspection && successSyncInspection) {
      handleSearch();
    }
  }, [successSyncInspection, loadingSyncInspection]);

  useEffect(() => {
    if (version === 'v2' && inspectionAmenities) {
      const params = new URLSearchParams(location.search);
      params.set('updated', inspectionAmenities.updated_at || '');
      navigate(
        {
          pathname: location.pathname,
          search: params.toString(),
        },
        { replace: true },
      );
    }
  }, [inspectionAmenities]);

  const value = useMemo(
    () => ({
      filteredProperties,
      inspectionAmenities,
      loadingInspectionAmenities,
      errorInspectionAmenities,
      loadingProperties,
      loadingSyncInspection,
      selectedProperty,
      selectedPropertyRef,
      handleChangeProperty,
      handleSearch,
      handleSyncInspection,
      searchAmenities,
      inspections,
      version,
    }),
    [
      filteredProperties,
      inspectionAmenities,
      loadingInspectionAmenities,
      errorInspectionAmenities,
      loadingProperties,
      loadingSyncInspection,
      selectedProperty,
      selectedPropertyRef,
      handleChangeProperty,
      handleSearch,
      handleSyncInspection,
      searchAmenities,
      inspections,
      version,
    ],
  );

  return <AmenitiesContext.Provider value={value}>{children}</AmenitiesContext.Provider>;
};
